import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination } from "react-bootstrap";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

import "./MobileShopScreen.css";

import { listProductsByShop } from "../../actions/productActions";
import Meta from "../../components/Meta";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import ProductCard from "../../components/ProductCard";
import MobileFilterPanel from "../../components/filteringComponent/MobileFilterPanel";
import { SORT_OPTIONS } from "../../components/filteringComponent/shopFilterOptions";
import { isValidShopKey, SHOPS } from "../../constants/shopMeta";
import { MTM_STEP_LABELS, normalizeMtmStep } from "../../utils/madeToMeasureJourney";
import { getServiceCatalogContext } from "../../utils/serviceCustomerCopy";

const PAGE_SIZE = 12;

const VALID_SORTS = new Set([
  "newest",
  "oldest",
  "price_asc",
  "price_desc",
  "name_asc",
  "name_desc",
]);

const EMPTY_FACETS = Object.freeze({
  colors: [],
  sizes: [],
  materials: [],
  patterns: [],
});

const REMOVED_FILTER_KEYS = [
  "brand",
  "brands",
  "unitType",
  "unitTypes",
  "finish",
  "finishes",
  "compatibility",
  "compatibilities",
  "keyword",
  "search",
  "q",
];

function parseCsvParam(value) {
  return [
    ...new Set(
      String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

function buildCsvParam(values = []) {
  return [
    ...new Set(
      values.map((value) => String(value || "").trim()).filter(Boolean),
    ),
  ].join(",");
}

function parsePositiveInt(value, fallback = 1) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.floor(parsed);
}

function normalizePriceParam(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  const parsed = Number(text);
  if (!Number.isFinite(parsed) || parsed < 0) return "";

  return String(parsed);
}

function normalizeIdParam(value) {
  return String(value || "").trim();
}

function normalizeSort(value) {
  const normalized = String(value || "").trim();
  return VALID_SORTS.has(normalized) ? normalized : "newest";
}

function cleanQueryParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }),
  );
}

function normalizeFacetArray(value) {
  return Array.isArray(value)
    ? [
        ...new Set(
          value.map((item) => String(item || "").trim()).filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b))
    : [];
}

function normalizeFacets(facets = {}) {
  return {
    ...EMPTY_FACETS,
    colors: normalizeFacetArray(facets.colors),
    sizes: normalizeFacetArray(facets.sizes),
    materials: normalizeFacetArray(facets.materials),
    patterns: normalizeFacetArray(facets.patterns),
  };
}

function countActiveFilters(filters) {
  let total = 0;

  ["colors", "sizes", "materials", "patterns"].forEach((key) => {
    total += filters[key]?.length || 0;
  });

  if (filters.minPrice || filters.maxPrice) total += 1;
  if (filters.inStock) total += 1;
  if (filters.sort && filters.sort !== "newest") total += 1;

  return total;
}

function normalizeFiltersFromSearchParams(searchParams) {
  return {
    category: normalizeIdParam(searchParams.get("category")),
    subCategory: normalizeIdParam(
      searchParams.get("subCategory") || searchParams.get("subcategory"),
    ),
    sort: normalizeSort(searchParams.get("sort")),
    minPrice: normalizePriceParam(searchParams.get("minPrice")),
    maxPrice: normalizePriceParam(searchParams.get("maxPrice")),
    inStock: searchParams.get("inStock") === "true",

    colors: parseCsvParam(searchParams.get("colors")),
    sizes: parseCsvParam(searchParams.get("sizes")),
    materials: parseCsvParam(searchParams.get("materials")),
    patterns: parseCsvParam(searchParams.get("patterns")),

    pageNumber: parsePositiveInt(searchParams.get("pageNumber"), 1),
  };
}

function getSortLabel(sort) {
  return (
    SORT_OPTIONS.find((option) => option.value === sort)?.label ||
    SORT_OPTIONS[0]?.label ||
    "Newest"
  );
}

function formatResultLabel(count) {
  const safeCount = Number(count || 0);
  return `${safeCount} ${safeCount === 1 ? "piece" : "pieces"}`;
}

function buildPaginationModel(currentPage, totalPages) {
  const current = parsePositiveInt(currentPage, 1);
  const total = parsePositiveInt(totalPages, 1);

  if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);

  const model = [1];

  if (current > 3) model.push("start-ellipsis");

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNumber = start; pageNumber <= end; pageNumber += 1) {
    model.push(pageNumber);
  }

  if (current < total - 2) model.push("end-ellipsis");

  model.push(total);

  return [...new Set(model)];
}

const MobileShopScreen = () => {
  const { shopKey = "" } = useParams();
  const normalizedShopKey = String(shopKey || "").trim();

  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filterOpen, setFilterOpen] = useState(false);
  const [initialOpenSection, setInitialOpenSection] = useState("");

  const isValidShop = useMemo(
    () => isValidShopKey(normalizedShopKey),
    [normalizedShopKey],
  );

  const currentShop = useMemo(
    () => SHOPS.find((shop) => shop.shopKey === normalizedShopKey) || null,
    [normalizedShopKey],
  );

  const searchParamsKey = searchParams.toString();
  const mtmStep = useMemo(
    () => normalizeMtmStep(searchParams.get("mtmStep")),
    [searchParams],
  );
  const isMtmSelectionMode = Boolean(mtmStep);

  const currentFilters = useMemo(
    () => normalizeFiltersFromSearchParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );

  const activeFilterCount = useMemo(
    () => countActiveFilters(currentFilters),
    [currentFilters],
  );

  const productList = useSelector((state) => state.productList || {});

  const {
    loading = false,
    error = "",
    products = [],
    page = 1,
    pages = 1,
    count = 0,
    facets = EMPTY_FACETS,
  } = productList;

  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products.filter(Boolean) : []),
    [products],
  );

  const safeFacets = useMemo(() => normalizeFacets(facets), [facets]);

  const serviceCatalogContext = useMemo(
    () =>
      getServiceCatalogContext({
        products: safeProducts,
        categoryId: currentFilters.category,
        subCategoryId: currentFilters.subCategory,
      }),
    [currentFilters.category, currentFilters.subCategory, safeProducts],
  );

  const serviceInfo = serviceCatalogContext.serviceInfo;
  const displayTitle =
    serviceCatalogContext.title || currentShop?.name || "Academy Costumes London";

  const requestParams = useMemo(
    () =>
      cleanQueryParams({
        shopKey: normalizedShopKey,
        category: currentFilters.category || undefined,
        subCategory: currentFilters.subCategory || undefined,
        sort: currentFilters.sort,

        minPrice: currentFilters.minPrice || undefined,
        maxPrice: currentFilters.maxPrice || undefined,

        inStock: currentFilters.inStock ? true : undefined,
        includeOutOfStock: currentFilters.inStock ? undefined : true,
        availability: currentFilters.inStock ? "in_stock" : "all",

        colors: buildCsvParam(currentFilters.colors),
        sizes: buildCsvParam(currentFilters.sizes),
        materials: buildCsvParam(currentFilters.materials),
        patterns: buildCsvParam(currentFilters.patterns),

        pageNumber: currentFilters.pageNumber,
        pageSize: PAGE_SIZE,
        mtmStep: mtmStep || undefined,
      }),
    [normalizedShopKey, currentFilters, mtmStep],
  );

  useEffect(() => {
    if (!isValidShop) return;
    dispatch(listProductsByShop(normalizedShopKey, requestParams));
  }, [dispatch, isValidShop, normalizedShopKey, requestParams]);

  useEffect(() => {
    if (!filterOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [filterOpen]);

  const updateParams = useCallback(
    (updates = {}, { resetPage = false, replace = true } = {}) => {
      const next = new URLSearchParams(searchParams);

      REMOVED_FILTER_KEYS.forEach((key) => next.delete(key));

      Object.entries(updates).forEach(([key, value]) => {
        const normalizedValue = Array.isArray(value)
          ? buildCsvParam(value)
          : String(value ?? "").trim();

        if (
          value === undefined ||
          value === null ||
          normalizedValue === "" ||
          (key === "inStock" && normalizedValue === "false")
        ) {
          next.delete(key);
        } else {
          next.set(key, normalizedValue);
        }
      });

      if (resetPage) next.delete("pageNumber");

      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (loading) return;
    if (currentFilters.pageNumber <= 1) return;

    const safeTotalPages = Math.max(1, Number(pages || 1));
    if (currentFilters.pageNumber <= safeTotalPages) return;

    updateParams(
      { pageNumber: safeTotalPages > 1 ? safeTotalPages : undefined },
      { replace: true },
    );
  }, [loading, currentFilters.pageNumber, pages, updateParams]);

  const openFilterPanel = useCallback((section = "") => {
    setInitialOpenSection(section);
    setFilterOpen(true);
  }, []);

  const handleApplyMobileFilters = useCallback(
    (nextFilters = {}) => {
      updateParams(
        {
          colors: nextFilters.colors || [],
          sizes: nextFilters.sizes || [],
          materials: nextFilters.materials || [],
          patterns: nextFilters.patterns || [],

          minPrice: nextFilters.minPrice || "",
          maxPrice: nextFilters.maxPrice || "",
          inStock: Boolean(nextFilters.inStock),
          sort: nextFilters.sort || "newest",

          brands: [],
          unitTypes: [],
          finishes: [],
          compatibilities: [],
        },
        { resetPage: true, replace: true },
      );

      setFilterOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [updateParams],
  );

  const clearAllFilters = useCallback(() => {
    const next = new URLSearchParams();
    if (currentFilters.category) next.set("category", currentFilters.category);
    if (currentFilters.subCategory) {
      next.set("subCategory", currentFilters.subCategory);
    }
    if (mtmStep) next.set("mtmStep", mtmStep);
    setSearchParams(next, { replace: true });
    setFilterOpen(false);
  }, [
    currentFilters.category,
    currentFilters.subCategory,
    mtmStep,
    setSearchParams,
  ]);

  const handlePageChange = useCallback(
    (nextPage) => {
      const parsed = parsePositiveInt(nextPage, 1);
      if (parsed === currentFilters.pageNumber) return;

      updateParams({ pageNumber: parsed }, { replace: false });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [currentFilters.pageNumber, updateParams],
  );

  const metaTitle = useMemo(() => {
    if (!currentShop) return "Shop | Academy Costumes London";

    const parts = [displayTitle];

    if (currentFilters.pageNumber > 1) {
      parts.push(`Page ${currentFilters.pageNumber}`);
    }

    parts.push("Academy Costumes London");
    return parts.join(" | ");
  }, [currentShop, currentFilters.pageNumber, displayTitle]);

  const metaDescription = useMemo(() => {
    if (!currentShop) {
      return "Explore premium fabrics and textiles from Academy Costumes London.";
    }

    return `Shop ${currentShop.name}. ${Number(count || 0)} items listed. ${
      currentShop.description || ""
    }`.trim();
  }, [currentShop, count]);

  const shouldNoIndex = activeFilterCount > 0 || currentFilters.pageNumber > 1;
  const resultLabel = useMemo(() => formatResultLabel(count), [count]);

  const contextLabel = useMemo(() => {
    if (isMtmSelectionMode) {
      return MTM_STEP_LABELS[mtmStep] || "Made-to-measure selection";
    }
    if (currentFilters.inStock) return "In-stock selection";
    if (activeFilterCount > 0) return "Refined selection";
    return "Curated collection";
  }, [activeFilterCount, currentFilters.inStock, isMtmSelectionMode, mtmStep]);

  const paginationModel = useMemo(
    () => buildPaginationModel(page, pages),
    [page, pages],
  );

  const paginationItems = useMemo(
    () =>
      paginationModel.map((item, index) => {
        if (typeof item !== "number") {
          return <Pagination.Ellipsis key={`${item}-${index}`} disabled />;
        }

        return (
          <Pagination.Item
            key={item}
            active={item === page}
            onClick={() => handlePageChange(item)}
            linkClassName="mobile-shop-screen__page-link"
          >
            {item}
          </Pagination.Item>
        );
      }),
    [handlePageChange, page, paginationModel],
  );

  if (!isValidShop) {
    return (
      <div className="mobile-shop-screen">
        <Meta
          title="Invalid Shop | Academy Costumes London"
          description="The requested shop could not be found."
          robots="noindex, follow"
          url={location.pathname}
        />
        <Message variant="danger">Invalid shop selected.</Message>
      </div>
    );
  }

  return (
    <div className="mobile-shop-screen">
      <Meta
        title={metaTitle}
        description={metaDescription}
        robots={shouldNoIndex ? "noindex, follow" : "index, follow"}
        url={location.pathname + location.search}
        products={safeProducts}
        breadcrumbs={[
          { name: "Home", url: "/" },
          {
            name: currentShop?.name || "Shop",
            url: `/shop/${normalizedShopKey}`,
          },
        ]}
      />

      <header
        className={clsx("mobile-shop-screen__masthead", {
          "is-refined": activeFilterCount > 0,
        })}
      >
        <div className="mobile-shop-screen__crumbs">
          Homepage <span>/</span> {currentShop?.name || "Shop"}
        </div>

        <h1 className="mobile-shop-screen__title">
          {displayTitle} <span>({Number(count || 0)})</span>
        </h1>

        {serviceInfo ? (
          <aside
            className="mobile-shop-screen__service-info"
            aria-label={serviceInfo.heading || serviceInfo.title}
          >
            {serviceInfo.heading ? <h2>{serviceInfo.heading}</h2> : null}

            {serviceInfo.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </aside>
        ) : currentShop?.description ? (
          <p className="mobile-shop-screen__description">
            {currentShop.description}
          </p>
        ) : null}

        <div className="mobile-shop-screen__context-row">
          <span>{contextLabel}</span>
          <strong>{resultLabel}</strong>
        </div>

        <div className="mobile-shop-screen__toolbar" aria-label="Shop controls">
          <button
            type="button"
            className={clsx("mobile-shop-screen__control-btn", {
              "is-active": activeFilterCount > 0,
            })}
            onClick={() => openFilterPanel("")}
          >
            <span className="mobile-shop-screen__control-icon" aria-hidden>
              ☷
            </span>
            <span>Filter</span>
            {activeFilterCount > 0 ? (
              <strong>{activeFilterCount}</strong>
            ) : null}
          </button>

          <button
            type="button"
            className={clsx("mobile-shop-screen__control-btn", {
              "is-active": currentFilters.sort !== "newest",
            })}
            onClick={() => openFilterPanel("sort")}
          >
            <span>Sort: {getSortLabel(currentFilters.sort)}</span>
            <span className="mobile-shop-screen__sort-caret" aria-hidden>
              ⌄
            </span>
          </button>
        </div>
      </header>

      <main className="mobile-shop-screen__body">
        {loading ? (
          <div className="mobile-shop-screen__state">
            <Loader />
          </div>
        ) : error ? (
          <div className="mobile-shop-screen__state">
            <Message variant="danger">{error}</Message>
          </div>
        ) : safeProducts.length === 0 ? (
          <div className="mobile-shop-screen__state">
            <Message variant="info">
              No products found for this shop with the current filters.
            </Message>
          </div>
        ) : (
          <>
            <div className="mobile-shop-screen__grid">
              {safeProducts.map((product) => (
                <article
                  key={product._id}
                  className="mobile-shop-screen__product"
                >
                  <ProductCard
                    product={product}
                    shopKey={normalizedShopKey}
                    mtmStep={mtmStep}
                    servicePricingContext={serviceInfo}
                  />
                </article>
              ))}
            </div>

            {pages > 1 ? (
              <nav
                className="mobile-shop-screen__pagination"
                aria-label="Shop pagination"
              >
                <Pagination>
                  <Pagination.Prev
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  />
                  {paginationItems}
                  <Pagination.Next
                    disabled={page >= pages}
                    onClick={() => handlePageChange(page + 1)}
                  />
                </Pagination>
              </nav>
            ) : null}
          </>
        )}
      </main>

      {filterOpen ? (
        <div
          className="mobile-shop-screen__filter-layer"
          role="dialog"
          aria-modal="true"
          aria-label="Shop filters"
        >
          <button
            type="button"
            className="mobile-shop-screen__filter-backdrop"
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
          />

          <aside className="mobile-shop-screen__filter-drawer">
            <MobileFilterPanel
              filters={currentFilters}
              facets={safeFacets}
              resultCount={count}
              initialOpenSection={initialOpenSection}
              onApply={handleApplyMobileFilters}
              onClearAll={clearAllFilters}
              onClose={() => setFilterOpen(false)}
            />
          </aside>
        </div>
      ) : null}
    </div>
  );
};

export default MobileShopScreen;
